declare global {
	interface Window {
		dataLayer?: unknown[];
	}
}

type DataLayerEvent = Record<string, unknown> & { event?: string };

const VIEWER_STYLES = `
	body { font-family: Arial, sans-serif; margin: 20px; background: #f5f5f5; }
	.header { background: #333; color: white; padding: 15px; margin: -20px -20px 20px -20px; font-size: 18px; font-weight: bold; }
	.counter { background: #2196f3; color: white; padding: 10px; border-radius: 5px; margin-bottom: 15px; font-weight: bold; }
	.data-layer { background: white; border: 1px solid #ddd; border-radius: 5px; padding: 15px; max-height: 500px; overflow-y: auto; }
	.event-item { border: 1px solid #e0e0e0; border-radius: 4px; margin-bottom: 10px; background: #fafafa; }
	.event-item--new { background: #e8f5e8; border: 2px solid #4caf50; }
	.new-badge { background: #4caf50; color: white; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 3px; margin-left: 8px; }
	.event-header { padding: 10px; background: #f0f0f0; cursor: pointer; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; align-items: center; user-select: none; }
	.event-header:hover { background: #e8e8e8; }
	.event-index { font-weight: bold; color: #666; margin-right: 5px; }
	.event-type { font-weight: bold; color: #2196f3; }
	.expand-icon { font-weight: bold; color: #666; }
	.event-content { padding: 15px; display: none; border-top: 1px solid #e0e0e0; }
	.event-content.expanded { display: block; }
	pre { margin: 0; white-space: pre-wrap; font-size: 12px; background: white; padding: 10px; border-radius: 3px; border: 1px solid #ddd; }
	.refresh-info { color: #666; font-size: 12px; margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
	.controls { display: flex; gap: 8px; }
	.controls button { color: white; border: none; padding: 8px 15px; border-radius: 4px; cursor: pointer; font-size: 12px; }
	.expand-all { background: #4caf50; }
	.collapse-all { background: #ff9800; }
`;

const VIEWER_SCRIPT = `
	function toggleEvent(index) {
		var content = document.getElementById('event-content-' + index);
		var icon = document.getElementById('expand-icon-' + index);
		var expanded = content.classList.toggle('expanded');
		icon.textContent = expanded ? '\u25bc' : '\u25b6';
	}
	function expandAll() {
		document.querySelectorAll('.event-content').forEach(function (c) { c.classList.add('expanded'); });
		document.querySelectorAll('.expand-icon').forEach(function (i) { i.textContent = '\u25bc'; });
	}
	function collapseAll() {
		document.querySelectorAll('.event-content').forEach(function (c) { c.classList.remove('expanded'); });
		document.querySelectorAll('.expand-icon').forEach(function (i) { i.textContent = '\u25b6'; });
	}
`;

const escapeHtml = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const safeStringify = (event: DataLayerEvent): string => {
	const seen = new WeakSet();
	try {
		return JSON.stringify(
			event,
			(_key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) {
						return "[Circular]";
					}
					seen.add(value);
				}
				return value;
			},
			2
		);
	} catch {
		return "Impossible d'afficher l'événement";
	}
};

/**
 * Returns the full HTML document for the dataLayer viewer window.
 */
export const buildViewerDocument = (): string => `
	<!DOCTYPE html>
	<html lang="fr">
		<head>
			<title>DataLayer Viewer - en temps réel</title>
			<style>${VIEWER_STYLES}</style>
		</head>
		<body>
			<div class="header">DataLayer Viewer - mise à jour en temps réel</div>
			<div class="refresh-info">
				<div>Mise à jour automatique toutes les secondes | tri : plus récent en haut</div>
				<div class="controls">
					<button class="expand-all" onclick="expandAll()">Tout expandre</button>
					<button class="collapse-all" onclick="collapseAll()">Tout collapser</button>
				</div>
			</div>
			<div class="counter" id="event-counter">Nombre d'événements : 0</div>
			<div class="data-layer" id="data-layer-container" data-previous-count="-1"><div>Chargement…</div></div>
			<script>${VIEWER_SCRIPT}</script>
		</body>
	</html>
`;

/**
 * Renders the current dataLayer into an already-open viewer window, preserving expanded rows.
 */
export const refreshViewer = (viewerWindow: Window, dataLayer: unknown[]): void => {
	const doc = viewerWindow.document;
	const counter = doc.getElementById("event-counter");
	const container = doc.getElementById("data-layer-container");

	if (!container) {
		return;
	}

	if (counter) {
		counter.textContent = `Nombre d'événements : ${dataLayer.length}`;
	}

	const previousCount = parseInt(container.dataset.previousCount ?? "-1", 10);

	if (dataLayer.length === previousCount) {
		return;
	}

	if (dataLayer.length === 0) {
		container.innerHTML = "<div>Aucun événement dans le dataLayer</div>";
		container.dataset.previousCount = "0";
		return;
	}

	const expanded = new Set<string>();
	container.querySelectorAll(".event-content.expanded").forEach(el => {
		expanded.add(el.id.replace("event-content-", ""));
	});

	const newCount = Math.max(0, dataLayer.length - previousCount);

	const rows = [...dataLayer].reverse().map((rawEvent, reverseIndex) => {
		const event = (rawEvent ?? {}) as DataLayerEvent;
		const originalIndex = dataLayer.length - 1 - reverseIndex;
		const eventType = escapeHtml(String(event.event ?? "Objet dataLayer"));
		const isExpanded = expanded.has(String(originalIndex));
		const isNew = reverseIndex < newCount;

		return `
			<div class="event-item ${isNew ? "event-item--new" : ""}" data-event-index="${originalIndex}">
				<div class="event-header" onclick="toggleEvent(${originalIndex})">
					<div>
						<span class="event-index">#${originalIndex + 1}</span>
						${isNew ? '<span class="new-badge">NOUVEAU</span>' : ""}
						- <span class="event-type">${eventType}</span>
					</div>
					<span class="expand-icon" id="expand-icon-${originalIndex}">${isExpanded ? "\u25bc" : "\u25b6"}</span>
				</div>
				<div class="event-content ${isExpanded ? "expanded" : ""}" id="event-content-${originalIndex}">
					<pre>${escapeHtml(safeStringify(event))}</pre>
				</div>
			</div>
		`;
	});

	const { scrollTop } = container;
	container.innerHTML = rows.join("");
	container.scrollTop = scrollTop;
	container.dataset.previousCount = String(dataLayer.length);
};
